import React, {useEffect, useState} from "react";
import { Box, Button, CardActions, CardContent, Chip, TextField } from "@mui/material";
import {useDispatch} from "react-redux";
import CategorySelect from "../input/CategorySelect";
import {deleteCategory, saveCategory} from "../../store/categorySlice";
import ViewAndEditFormLayout from "./ViewAndEditFormLayout";
import {useTranslation} from "react-i18next";

const categoryTypeLabelKey = (type) => {
    switch ((type || "").toUpperCase()) {
        case "EXPENSE": return "common.transactionType.expense";
        case "INCOME": return "common.transactionType.income";
        default: return null;
    }
};

const categoryTypeColor = (type) => {
    switch ((type || "").toUpperCase()) {
        case "EXPENSE": return "error";
        case "INCOME": return "success";
        default: return "default";
    }
};



const EditCategoryForm = ({category, onCancel}) => {
    const {t} = useTranslation();
    const DEFAULT_ERRORS = {name: null};
    const dispatch = useDispatch();
    const [name, setName] = useState(category.name);
    const [parentId, setParentId] = useState(null);
    const [errors, setErrors] = useState(DEFAULT_ERRORS);

    useEffect(() => {
        setName(category.name);
        category.parentId ? setParentId(category.parentId) : setParentId(null);
        setErrors(DEFAULT_ERRORS);
    }, [category]);

    const validateForm = () => {
        const nextErrors = {
            name: !name || !name.trim() ? "Name is required" : null
        };
        setErrors(nextErrors);
        return !nextErrors.name;
    };

    const typeLabelKey = categoryTypeLabelKey(category.type);

    const editContent = ({ onCancel: handleCancel, onSave: handleSave }) => (
        <>
            <CardContent>
                {typeLabelKey && (
                    <Box sx={{ m: 2, display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ color: "text.secondary" }}>{t("common.labels.type")}:</Box>
                        <Chip
                            label={t(typeLabelKey)}
                            color={categoryTypeColor(category.type)}
                            size="small"
                            variant="outlined"
                        />
                    </Box>
                )}
                <Box sx={{ m: 2 }}>
                    <TextField
                        id="name"
                        value={name}
                        onChange={(e) => {
                            setName(e.target.value);
                            setErrors(prev => ({ ...prev, name: null }));
                        }}
                        label={t("common.labels.name")}
                        variant="standard"
                        sx={{ width: "70%" }}
                        error={!!errors.name}
                        helperText={errors.name}
                    />
                </Box>
                <Box sx={{ m: 2 }}>
                    <CategorySelect
                        categoryType={category.type}
                        selectedCategoryId={parentId}
                        label={t("forms.category.parentCategory")}
                        excludeCategoryId={category.id}
                        onChange={(categoryId) => setParentId(categoryId)}
                    />
                </Box>
            </CardContent>
            <CardActions sx={{ ml: "2rem", mb: "1rem", mt: "1rem", mr: "2rem", height: "4rem" }}>
                <Box sx={{ position: "relative", width: "100%" }}>
                    <Box sx={{ position: "absolute", left: 0 }}>
                        <Button variant="outlined" sx={{ mr: "1rem" }} onClick={handleCancel}>{t("common.buttons.cancel")}</Button>
                        <Button variant="contained" onClick={handleSave}>{t("common.buttons.save")}</Button>
                    </Box>
                    {category.id && (
                        <Box sx={{ position: "absolute", right: 0 }}>
                            <Button variant="outlined" color="error" onClick={() => dispatch(deleteCategory(category, () => onCancel()))}>
                                {t("common.buttons.delete")}
                            </Button>
                        </Box>
                    )}
                </Box>
            </CardActions>
        </>
    );

    return (
        <ViewAndEditFormLayout
            editTitle={category.id ? t("forms.category.editTitle") : t("forms.category.createTitle")}
            viewTitle={category.id ? t("forms.category.editTitle") : t("forms.category.createTitle")}
            initialEdit={true}
            onCancel={onCancel}
            onSave={() =>
                validateForm() && dispatch(
                    saveCategory(
                        { ...category, name: name.trim(), parentId: parentId ? parentId : null },
                        () => onCancel()
                    )
                )
            }
            onDelete={category.id ? () => dispatch(deleteCategory(category, () => onCancel())) : undefined}
            editContent={editContent}
            viewContent={null}
        />
    );
}
export default EditCategoryForm